import { RouteLink } from "@/components/ui";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { adminService } from "@/api/admin/admin.service";
import type { TWhatsAppGroupDetail } from "@/api/admin/admin.types";
import { extractErrorMessage } from "@/utils/errorUtils";

type TProps = {
  groupId: string;
};

export function WhatsAppGroupDetailPage({ groupId }: TProps) {
  const [group, setGroup] = useState<TWhatsAppGroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const { error: err, data: res } = await adminService.getWhatsAppGroup(groupId);
    if (err) {
      setError(await extractErrorMessage(err));
      setGroup(null);
    } else {
      // API returns TApiResponse<TWhatsAppGroupDetail>; ky's .json() loses precise typing
      setGroup(res?.data ?? null);
    }
    setLoading(false);
  }, [groupId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSend = async () => {
    const text = messageText.trim();
    if (!text || sending) return;
    setSending(true);
    const { error: err } = await adminService.sendWhatsAppGroupMessage(groupId, { text });
    setSending(false);
    if (err) {
      setError(await extractErrorMessage(err));
      return;
    }
    setMessageText("");
    void load();
  };

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden bg-gray-50">
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="mx-auto max-w-4xl space-y-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-8 w-8 rounded-md" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
            <Card>
              <CardHeader>
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} className="h-16 w-full rounded-lg" />
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="h-full overflow-y-auto bg-gray-50 p-4 sm:p-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-red-600">{error}</p>
            <RouteLink to="/app/admin/whatsapp-groups" className="mt-4 inline-block">
              <Button variant="outline">Back to groups</Button>
            </RouteLink>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!group) {
    return null;
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-gray-50">
      <div className="flex-1 overflow-y-auto p-4 sm:p-6">
        <div className="mx-auto max-w-4xl space-y-4">
          <div className="flex items-center gap-4">
            <RouteLink to="/app/admin/whatsapp-groups">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </RouteLink>
            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">{group.name}</h1>
              <p className="text-sm text-gray-600">
                {group.investor_company_name ?? "—"} + {group.fundraiser_company_name ?? "—"}
              </p>
            </div>
          </div>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Messages</CardTitle>
              <CardDescription>
                Inbound and outbound messages. Send as Fundy using the input below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {group.messages.length === 0 ? (
                  <p className="py-8 text-center text-sm text-gray-500">No messages yet.</p>
                ) : (
                  [...group.messages].reverse().map((m) => (
                    <div
                      key={m.id}
                      className={`rounded-lg p-3 ${
                        m.direction === "outbound" ? "ml-8 bg-primary/10" : "mr-8 bg-gray-100"
                      }`}
                    >
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span>{m.direction === "outbound" ? "Fundy" : m.sender_phone ?? "Unknown"}</span>
                        {m.created_at && <span>{new Date(m.created_at).toLocaleString()}</span>}
                      </div>
                      <p className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{m.content}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Send as Fundy</CardTitle>
              <CardDescription>Type a message to send to the group as Fundy.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <textarea
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Type your message..."
                  className="min-h-[80px] flex-1 resize-y rounded-lg border border-gray-300 p-3 text-sm"
                  rows={3}
                  disabled={sending}
                />
                <Button
                  onClick={() => void handleSend()}
                  disabled={!messageText.trim() || sending}
                  className="self-end"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  Send
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
